import styled from '@emotion/styled';
import { Paper } from '@material-ui/core';
import { createElement, FC, Fragment, useContext } from 'react';
import { useRecoilValue } from 'recoil';

import { SPACING_HUGE, SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import { context as DependencyContext } from '../../../context/dependency';
import {
  editingProjectBriefFilesStateAtom,
  editingProjectStateAtom,
  editingProjectStateErrorsAtom,
} from '../../../page/ProjectEditingPage/atoms/editingProjectStateAtom';
import { getErrors } from '../../../page/ProjectEditingPage/domain/stateValidator';
import { useBriefFiles } from '../../../page/ProjectEditingPage/hooks/useBriefFiles';
import { useEditingProjectState } from '../../../page/ProjectEditingPage/hooks/useEditingProjectState';
import { gridLayoutItem } from '../../../styleUtility/gridLayout';
import { ExtendedProject } from '../../../types/ExtendedProject';
import Field from './Field';
import Section from './Section';

type Props = {
  project: ExtendedProject | null;
};

const CreativeBriefEditor: FC<Props> = ({ project }) => {
  const { briefFiles, uploadingBriefFiles, uploadingFilesProgress } = useRecoilValue(editingProjectBriefFilesStateAtom);
  const { updateCreativeBriefValue } = useEditingProjectState();
  const state = useRecoilValue(editingProjectStateAtom);
  const errors = useRecoilValue(editingProjectStateErrorsAtom);
  const {
    editProjectUsecase: { deleteBriefFile, uploadBriefFiles },
  } = useContext(DependencyContext);
  const {
    addUploadingBriefFiles,
    removeUploadingBriefFile,
    removeUploadingFilesProgress,
    updateUploadingFilesProgress,
    addBriefFile,
    removeBriefFile,
  } = useBriefFiles();

  const creativeBriefValue = state.creativeBriefValue;
  const creativeBriefDefinition = project?.creativeBriefDefinition;

  return (
    <Container>
      <Form>
        {creativeBriefDefinition && creativeBriefValue && errors ? (
          <>
            {creativeBriefDefinition.sections.map((section) => {
              const fields = section.fields.map((field) => {
                return (
                  <Field
                    field={field}
                    value={creativeBriefValue.values[field.key]}
                    files={briefFiles[field.key]}
                    uploadingFiles={uploadingBriefFiles[field.key]}
                    uploadingFilesProgress={uploadingFilesProgress}
                    errors={getErrors(errors)[field.key] || []}
                    onChange={(_, value) => updateCreativeBriefValue(field.key, value)}
                    onRequestDeleteFile={(_, briefFile, bucketId) =>
                      deleteBriefFile({
                        fileReference: briefFile,
                        values: creativeBriefValue.values,
                        bucketId,
                        removeBriefFile,
                      })
                    }
                    onRequestUploadFiles={(_, files, bucketId) =>
                      uploadBriefFiles({
                        files: [...files],
                        values: creativeBriefValue.values,
                        bucketId,
                        addUploadingBriefFiles,
                        updateUploadingFilesProgress,
                        handleFileUploadFinished: async (file, fileReference, creativeBriefFieldKey) => {
                          await removeUploadingFilesProgress(file.id);
                          await removeUploadingBriefFile(file, creativeBriefFieldKey);
                          await addBriefFile(fileReference, creativeBriefFieldKey);
                        },
                      })
                    }
                    key={field.key}
                  />
                );
              });

              return (
                <StyledSection section={section} key={section.id}>
                  {fields}
                </StyledSection>
              );
            })}
          </>
        ) : null}
      </Form>
    </Container>
  );
};

const StyledSection = styled(Section)`
  width: 100%;
  margin-bottom: ${SPACING_HUGE}px;
`;

const Container = styled(Paper)`
  margin-top: ${SPACING_REGULAR}px;
  margin-bottom: ${SPACING_HUGE}px;
  padding: ${SPACING_REGULAR}px;
`;

const Form = styled('div')`
  ${gridLayoutItem('form')}
  margin-bottom: ${SPACING_UNIT * 2}px;
  padding: 0 calc((100% - 1280px) / 2);
`;

export default CreativeBriefEditor;
